import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import BrandMark from "@/components/BrandMark";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const initialToken = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState(initialToken ? "reset" : "request"); // request | sent | reset | done
  const [token, setToken] = useState(initialToken);
  const [newPw, setNewPw] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initialToken) setStage("reset");
  }, [initialToken]);

  const requestReset = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.reset_token) {
        // Email failed (e.g. Resend testing mode). Fall back to inline token.
        setToken(data.reset_token);
        setStage("reset");
        setMsg("We couldn't send an email right now — use this one-time token to set a new password.");
      } else {
        setStage("sent");
        setMsg("If that email is registered, we've sent a reset link. Check your inbox (and spam folder).");
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: newPw });
      setStage("done");
      setMsg("Password reset successful. You can sign in now.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#071426] px-4">
      <div className="w-full max-w-md fade-up">
        <div className="flex justify-center mb-8">
          <BrandMark />
        </div>
        <div className="bg-[#0D2038] border border-[#1F3A5C] rounded-2xl p-7">
          <h1 className="font-heading text-2xl font-bold mb-1" data-testid="forgot-title">Reset password</h1>

          {stage === "request" && (
            <form onSubmit={requestReset} className="space-y-4 mt-5">
              <p className="text-sm text-[#AAB7C7]">Enter your email and we'll send you a reset link.</p>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input"
                placeholder="you@example.com"
                className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
              {err && <div className="text-sm text-red-400" data-testid="forgot-error">{err}</div>}
              <button type="submit" disabled={busy} data-testid="forgot-submit-btn"
                className="w-full py-3 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-60 font-semibold text-[#071426]">
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          )}

          {stage === "sent" && (
            <div className="mt-5 space-y-3" data-testid="forgot-sent">
              <div className="text-sm p-3 rounded-lg bg-[#C7F500]/10 border border-[#C7F500]/30 text-[#C7F500]">
                Check your inbox.
              </div>
              <p className="text-sm text-[#AAB7C7]">{msg}</p>
              <p className="text-xs text-[#8397AC]">Didn't receive it? Check your spam folder, or{" "}
                <button onClick={() => setStage("request")} className="text-[#C7F500] hover:underline">try another email</button>.
              </p>
            </div>
          )}

          {stage === "reset" && (
            <form onSubmit={doReset} className="space-y-4 mt-5">
              {msg && (
                <div className="text-xs p-3 rounded-lg bg-[#C7F500]/10 border border-[#C7F500]/30 text-[#C7F500]">
                  {msg}
                </div>
              )}
              <input type="password" required minLength={6} placeholder="New password"
                value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-password-input"
                className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
              {err && <div className="text-sm text-red-400" data-testid="reset-error">{err}</div>}
              <button type="submit" disabled={busy} data-testid="reset-submit-btn"
                className="w-full py-3 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-60 font-semibold text-[#071426]">
                {busy ? "Updating…" : "Set new password"}
              </button>
            </form>
          )}

          {stage === "done" && (
            <div className="mt-5 text-sm text-[#AAB7C7]" data-testid="reset-done">
              {msg} <Link to="/login" className="text-[#C7F500] hover:underline">Sign in →</Link>
            </div>
          )}

          <div className="mt-6 text-sm text-center text-[#AAB7C7]">
            <Link to="/login" className="text-[#C7F500] hover:underline" data-testid="back-to-login">Back to login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
